import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Eye, Heart } from "lucide-react";
import { api } from "../lib/api";
import { SeriesEmblem } from "../components/SeriesEmblem";
import { BlackCat } from "../components/BlackCat";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

export default function PostPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [felt, setFelt] = useState(false);
  const [feltCount, setFeltCount] = useState(null);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? Math.min(1, el.scrollTop / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setData(null);
    setMissing(false);
    setFelt(!!localStorage.getItem(`felt-${slug}`));
    setFeltCount(null);
    api.get(`/posts/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setMissing(true));
  }, [slug]);

  const doFelt = async () => {
    if (felt) return;
    try {
      const { data } = await api.post(`/posts/${slug}/felt`);
      setFeltCount(data.reactions);
      setFelt(true);
      localStorage.setItem(`felt-${slug}`, "1");
    } catch {}
  };

  if (missing) {
    return (
      <div className="min-h-screen bg-[#070709] flex flex-col items-center justify-center gap-6" data-testid="post-not-found">
        <p className="font-serif text-3xl text-[#ede8e1]">This page was never written.</p>
        <Link to="/" className="font-mono text-xs tracking-[0.25em] uppercase text-[#581313]">Return to the Vault</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#070709] flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.4em] uppercase text-[#78716c] flicker">Unsealing…</p>
      </div>
    );
  }

  const { post, section, related, series_posts, series_emblem } = data;
  const words = (post.body || "").split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  const feltTotal = feltCount ?? post.reactions ?? 0;
  // Frame motif — keeper's choice from the Sanctum, else stable per series (or per essay)
  const motif = post.motif !== undefined && post.motif !== "" && post.motif !== null
    ? Number(post.motif)
    : (post.series || post.slug).split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 3;

  return (
    <div className="relative min-h-screen" data-testid="post-page">
      <div style={{ transform: `scaleX(${progress})` }} data-testid="reading-progress-bar"
        className="fixed top-0 left-0 right-0 h-[3px] bg-[#581313] origin-left z-[70] transition-transform duration-150 ease-out" />
      <Navbar />
      <main className="relative z-10 pt-36 pb-24">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 sm:py-10 sm:my-[-2.5rem]">
          <div className={`essay-frame motif-${motif}`} aria-hidden="true" data-testid="essay-frame">
            <span className="ef-c ef-tl" /><span className="ef-c ef-tr" />
            <span className="ef-c ef-bl" /><span className="ef-c ef-br" />
            <span className="ef-motif" />
          </div>
          <article className="relative">
            <Link to={section ? `/s/${section.slug}` : "/"} data-testid="post-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716c] hover:text-[#ede8e1] transition-colors">
              <ArrowLeft size={14} /> {section?.title || "The Vault"}
            </Link>
            <motion.header initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-10 mb-12">
              <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-5">
                {new Date(post.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
                {" — "}{minutes} MIN READ
              </p>
              <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#ede8e1]" data-testid="post-title">
                {post.title}
              </h1>
              {post.excerpt && <p className="mt-5 text-lg italic font-light text-[#a8a29e]">{post.excerpt}</p>}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {(post.tags || []).map((t) => (
                  <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c] border border-[#232530] px-2 py-1">{t}</span>
                ))}
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c]" data-testid="post-view-count">
                  <Eye size={13} /> {post.views || 0} reads
                </span>
              </div>
            </motion.header>
            {series_posts && series_posts.length > 1 && (
              <motion.nav initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="border border-[#232530] bg-[#0d0e12] p-6 mb-12" data-testid="series-nav">
                <div className="flex items-center justify-between mb-4">
                  <p className="flex items-center gap-3 font-mono text-xs tracking-[0.35em] uppercase text-[#581313]">
                    <SeriesEmblem name={post.series} emblem={series_emblem} size={34} /> Series — {post.series}
                  </p>
                  <Link to={`/series/${encodeURIComponent(post.series)}`} data-testid="series-toc-link"
                    className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716c] hover:text-[#ede8e1] transition-colors">
                    Table of contents
                  </Link>
                </div>
                <ol className="space-y-2">
                  {series_posts.map((sp, i) => (
                    <li key={sp.id} className="font-serif text-lg">
                      {sp.slug === post.slug ? (
                        <span className="text-[#ede8e1]" data-testid={`series-current-${sp.slug}`}>
                          <span className="font-mono text-xs text-[#581313] mr-3">{String(i + 1).padStart(2, "0")}</span>
                          {sp.title} <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c]">— you are here</span>
                        </span>
                      ) : (
                        <Link to={`/post/${sp.slug}`} data-testid={`series-chapter-${sp.slug}`}
                          className="text-[#a8a29e] hover:text-[#ede8e1] transition-colors">
                          <span className="font-mono text-xs text-[#57534e] mr-3">{String(i + 1).padStart(2, "0")}</span>
                          {sp.title}
                        </Link>
                      )}
                    </li>
                  ))}
                </ol>
              </motion.nav>
            )}
            {post.cover_image && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.9 }}
                className="image-frame mb-14">
                <img src={post.cover_image} alt={post.title} className="w-full h-[380px] object-cover grayscale-[25%] contrast-110" data-testid="post-cover" />
              </motion.div>
            )}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="prose-gothic" data-testid="post-body">
              <ReactMarkdown>{post.body || "*This entry holds only its title — for now.*"}</ReactMarkdown>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-16 flex justify-center">
              <button onClick={doFelt} disabled={felt} data-testid="felt-this-button"
                className={`group flex items-center gap-3 border px-8 py-3.5 font-mono text-xs uppercase tracking-widest transition-colors ${
                  felt
                    ? "border-[#581313] bg-[#581313] text-[#ede8e1]"
                    : "border-[#232530] bg-[#0d0e12] text-[#a8a29e] hover:border-[#581313] hover:text-[#ede8e1]"
                }`}>
                <Heart size={15} fill={felt ? "#ede8e1" : "none"} />
                {felt ? `Felt — ${feltTotal}` : `Felt this${feltTotal > 0 ? ` — ${feltTotal}` : ""}`}
              </button>
            </motion.div>
          </article>
        </div>
        {related && related.length > 0 && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-20 border-t border-[#232530] pt-4" data-testid="related-essays">
            <div className="flex justify-center mb-6">
              <BlackCat seed={post.slug} pose={post.cat} size={48} inline />
            </div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-8 text-center">Kindred pieces</p>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.id} to={`/post/${r.slug}`} data-testid={`related-${r.slug}`}
                  className="card-lift border border-[#232530] bg-[#0d0e12] p-5 group">
                  <h3 className="font-serif text-lg leading-snug text-[#ede8e1] group-hover:text-[#c9c4bc] transition-colors">{r.title}</h3>
                  {r.excerpt && <p className="mt-2 text-sm font-light text-[#a8a29e] line-clamp-2">{r.excerpt}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(r.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c]">{t}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
